import { cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, RefreshCw } from "lucide-react";
import { CompactTraceItem } from "./CompactTraceItem";
import { useCompactTracesData } from "./useCompactTracesData";

interface CompactTracesListProps {
  projectId: string;
}

export function CompactTracesList({ projectId }: CompactTracesListProps) {
  const navigate = useNavigate();
  const { compactTraces, isLoading, error } = useCompactTracesData(projectId);

  const handleTraceClick = (traceId: string) => {
    // Navigate to observability tab with specific trace expanded
    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: { tab: "observability", expandedTrace: traceId },
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading traces...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">Error loading traces</span>
        </div>
      </div>
    );
  }

  // Empty state
  if (compactTraces.length === 0) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-center space-y-2">
          <AlertCircle className="w-6 h-6 mx-auto text-muted-foreground/50" />
          <div className="text-sm font-medium text-muted-foreground">
            No traces yet
          </div>
          <div className="text-xs text-muted-foreground max-w-48">
            Traces will appear here when your MCP server is used
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="@container">
      {/* Table Header */}
      <div
        className={cn(
          "grid gap-2 px-3 py-2 text-xs font-medium text-muted-foreground border-b bg-muted/30",
          // Responsive grid: minimal (3 cols) → small (4 cols)
          "grid-cols-[32px_1fr_60px]",
          "@[400px]:grid-cols-[32px_2fr_1fr_60px]",
        )}
      >
        <div className="text-center">•</div>

        <div className="hidden @[400px]:block">Request</div>
        <div className="hidden @[400px]:block">Target</div>
        <div className="block @[400px]:hidden">Request / Target</div>

        <div className="text-right">Time</div>
      </div>

      {/* Trace Rows */}
      <div className="divide-y-0">
        {compactTraces.map((trace) => (
          <CompactTraceItem
            key={trace.traceId}
            trace={trace}
            onClick={() => handleTraceClick(trace.traceId)}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 text-xs text-muted-foreground border-t bg-muted/10">
        <div className="flex justify-between items-center">
          <span>
            {compactTraces.length} recent trace
            {compactTraces.length !== 1 ? "s" : ""}
          </span>
          <span className="hidden @[400px]:block">Click to view details</span>
        </div>
      </div>
    </div>
  );
}
